import { formatQty } from "@buildpy/core/lib/format";
import type { ChangeOrderSnapshotData } from "@buildpy/core/modules/change-order/types";
import { CHANGE_ORDER_DIRECTION_LABEL } from "@buildpy/core/modules/change-order/types";
import { changeOrderSnapshotVatModeOf, documentSourceTagOf, extendDaysLabel, paymentCompareRows } from "@buildpy/core/modules/change-order/view";
import { VAT_LABEL, vatBasisLabel } from "@buildpy/core/modules/quote/snapshot-vat";
import Money from "@/components/money";
import PrintSheet, { PrintUnit, SheetField, SheetTitle, TD, TH } from "@/modules/print/sheet";

interface Props {
  data: ChangeOrderSnapshotData;
  // 작성 중 미리보기 — 고객이 승인한 문서가 아님을 종이에 박는다.
  draft: boolean;
  docHash: string | null;
}

// 변경계약서 한 장 — 스냅샷(고객이 승인하는 문서) 숫자만 찍는다.
const ChangeOrderDocument = ({ data, draft, docHash }: Props) => {
  const excl = changeOrderSnapshotVatModeOf(data) === "EXCL";
  return (
    <PrintSheet>
      {draft && (
        <div className="mb-3 border border-[#d4a72c] bg-[#fff8e1] px-2.5 py-1.5 text-xs leading-5">
          작성 중 미리보기 — 보내거나 승인하기 전의 문서입니다.
        </div>
      )}
      <SheetTitle>변경계약서</SheetTitle>
      <p className="mb-3 text-right text-xs text-[#525252]">
        계약번호 {data.contract_no} · 변경 {data.seq}차
      </p>
      <div className="border-t border-[#d9d9d9]">
        <SheetField label="공사명">{data.contract_title}</SheetField>
        <SheetField label="도급인">{data.client_name}</SheetField>
        <SheetField label="수급인">{data.contractor_name}</SheetField>
        <SheetField label="변경 제목">{data.title}</SheetField>
        {data.reason && (
          <SheetField label="변경 사유">
            <span className="whitespace-pre-wrap">{data.reason}</span>
          </SheetField>
        )}
        {data.extend_days ? <SheetField label="공기 증감">{extendDaysLabel(data.extend_days)}</SheetField> : null}
      </div>

      <p className="mb-1 mt-4 text-xs font-semibold">변경 항목{excl && ` (단가·금액 ${vatBasisLabel("EXCL")})`}</p>
      {data.lines.length === 0 ? (
        <p className="text-xs">금액 변경 항목 없음</p>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <TH>구분</TH>
              <TH>항목</TH>
              <TH>수량</TH>
              <TH>재료 단가</TH>
              <TH>노무 단가</TH>
              <TH>금액</TH>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((l) => {
              const tag = documentSourceTagOf(l);
              return (
              <tr key={l.seq}>
                <TD className="text-center">{CHANGE_ORDER_DIRECTION_LABEL[l.direction]}</TD>
                <TD>
                  {l.item}
                  {l.note && <span className="ml-1 text-[#525252]">({l.note})</span>}
                  {tag && (
                    <span className="block text-[10px] text-[#525252]" data-testid="change-order-line-source">
                      {tag}
                    </span>
                  )}
                </TD>
                <TD className="text-right tabular-nums">
                  {formatQty(l.qty)} <PrintUnit unit={l.unit} />
                </TD>
                <TD className="text-right tabular-nums"><Money value={l.material_unit_price} /></TD>
                <TD className="text-right tabular-nums"><Money value={l.labor_unit_price} /></TD>
                <TD className="text-right tabular-nums"><Money value={l.direction === "DEDUCT" ? -l.amount : l.amount} signed /></TD>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <p className="mb-1 mt-4 text-xs font-semibold">계약금액 (부가세 포함)</p>
      <div className="border-t border-[#d9d9d9]">
        <SheetField label="변경 전">
          <Money value={data.base_total} />
        </SheetField>
        {excl ? (
          <>
            <SheetField label="공급가 증감">
              <Money value={data.delta_supply} signed />
            </SheetField>
            <SheetField label={VAT_LABEL}>
              <Money value={data.delta_vat} signed />
            </SheetField>
            <SheetField label="증감 합계">
              <Money value={data.delta_total} signed />
            </SheetField>
          </>
        ) : (
          <SheetField label="증감">
            <Money value={data.delta_total} signed /> (공급가 <Money value={data.delta_supply} signed /> · 부가세{" "}
            <Money value={data.delta_vat} signed />)
          </SheetField>
        )}
        <SheetField label="변경 후">
          <Money value={data.after_total} className="font-semibold" />
        </SheetField>
      </div>

      <p className="mb-1 mt-4 text-xs font-semibold">지급회차</p>
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <TH>회차</TH>
            <TH>변경 전</TH>
            <TH>변경 후</TH>
            <TH>지급</TH>
          </tr>
        </thead>
        <tbody>
          {paymentCompareRows(data).map((p) => (
            <tr key={p.seq}>
              <TD>{p.label}</TD>
              <TD className="text-right tabular-nums"><Money value={p.before} /></TD>
              <TD className="text-right tabular-nums"><Money value={p.after} /></TD>
              <TD className="text-center">{p.paid ? "지급됨" : ""}</TD>
            </tr>
          ))}
        </tbody>
      </table>

      {docHash && <p className="mt-4 break-all text-[10px] text-[#737373]">문서 해시 {docHash}</p>}
    </PrintSheet>
  );
};

export default ChangeOrderDocument;
